import { useEffect, useRef } from 'react'

import { useTelemetrySource } from '../../lib/api/source.tsx'
import { formatInt } from '../../lib/format.ts'
import { LOG_BUFFER, useLogStream } from '../../lib/sse/useLogStream.ts'
import type { LogFilter } from '../../lib/sse/types.ts'
import { Badge } from '../primitives/Badge.tsx'
import { Button } from '../primitives/Button.tsx'
import { ConnectionBadge } from '../primitives/ConnectionStatus.tsx'
import { Metric } from '../primitives/Metric.tsx'
import { Notice } from '../primitives/States.tsx'
import { useToast } from '../primitives/Toast.tsx'
import { LogTable } from './LogTable.tsx'

/**
 * O tail ao vivo de um recorte.
 *
 * A honestidade da amostragem é o coração desta tela (§4.3): mostra a vazão real
 * do filtro ao lado do que está sendo exibido, marca cobertura parcial (§4.5),
 * trata `lapped` como alarme (§4.3) e distingue "máquina quieta" de "histórico
 * indisponível" (§4.7). Num EDR, uma tela vazia lida como "tudo calmo" é o pior
 * modo de falha que existe.
 */
export function LogStreamView({
  filter,
  onOpenHistory,
}: {
  filter: LogFilter
  /** Oferecido em cobertura parcial e quando o buffer descarta — o histórico é completo. */
  onOpenHistory?: () => void
}) {
  const toast = useToast()
  const source = useTelemetrySource()
  const { connection, rows, frame, backlogDegraded, rejected, lastEventAt } = useLogStream(
    filter,
    source.streamUrl,
  )

  // `lapped` é o servidor admitindo que não acompanhou o próprio buffer — alarme
  // operacional, não nota de rodapé (§4.3). Avisa uma vez por transição para 0→>0.
  const lastLapped = useRef(0)
  useEffect(() => {
    const lapped = frame?.lapped ?? 0
    if (lapped > 0 && lastLapped.current === 0) {
      toast.failure(
        `${formatInt(lapped)} eventos perdidos no quadro.`,
      )
    }
    lastLapped.current = lapped
  }, [frame?.lapped, toast])

  if (connection === 'forbidden') {
    return (
      <div className="flex flex-1 flex-col items-start gap-2 p-6">
        <h2 className="text-base font-medium text-fg">Sem permissão nesta área.</h2>
      </div>
    )
  }

  const rate =
    frame !== null && frame.window_ms > 0 ? frame.matched / (frame.window_ms / 1000) : null
  const sampling = frame !== null && frame.sampled_out > 0
  const partial = frame !== null && frame.coverage !== 'full'
  const bufferFull = rows.length >= LOG_BUFFER

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Barra de vazão + cobertura + conexão. */}
      <div className="flex shrink-0 flex-wrap items-center gap-x-6 gap-y-2 border-b border-line-subtle px-3 py-2">
        <Metric
          label="Vazão do filtro"
          value={rate === null ? '—' : formatInt(Math.round(rate))}
          unit="ev/s"
          tone={sampling ? 'warn' : 'neutral'}
          note={
            frame === null
              ? 'aguardando o primeiro quadro'
              : sampling
                ? `exibindo ${formatInt(frame.delivered)} de ${formatInt(frame.matched)}`
                : 'fluxo completo'
          }
        />

        {partial && frame !== null && (
          <div className="flex items-center gap-2">
            <Badge tone="warn" dot>
              Cobertura parcial · {formatInt(frame.instances)} instâncias
            </Badge>
            {onOpenHistory !== undefined && (
              <Button size="sm" onClick={onOpenHistory}>
                Consultar histórico
              </Button>
            )}
          </div>
        )}

        <div className="ml-auto flex items-center gap-3">
          {rejected > 0 && (
            <span className="text-micro text-warn">
              {formatInt(rejected)} rejeitados
            </span>
          )}
          <ConnectionBadge state={connection} lastEventAt={lastEventAt} />
        </div>
      </div>

      {/* Avisos que NÃO podem virar tela vazia enganosa. */}
      <div className="shrink-0 space-y-1 px-3 pt-2 empty:hidden">
        {backlogDegraded && (
          <Notice tone="warn">Histórico indisponível: não foi possível ler os eventos anteriores.</Notice>
        )}
        {bufferFull && (
          <Notice tone="neutral">
            Mostrando as {formatInt(LOG_BUFFER)} linhas mais recentes.
            {onOpenHistory !== undefined && (
              <>
                {' '}
                <button type="button" onClick={onOpenHistory} className="underline">
                  Consultar histórico
                </button>
              </>
            )}
          </Notice>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pt-2">
        {rows.length === 0 && (connection === 'connecting' || connection === 'disconnected') ? (
          <p role="status" className="px-3 py-8 text-sm text-fg-muted">
            {connection === 'connecting'
              ? 'Conectando ao stream…'
              : 'Conexão perdida — tentando reconectar.'}
          </p>
        ) : (
          <LogTable rows={rows} />
        )}
      </div>
    </div>
  )
}
